import { toast } from 'react-toastify';
import type { Donation } from './sdk/donations';

export interface DonationReceiptPayload {
  receiptId: number;
  amount: number;
  ref?: string;
  donationTypeName: string;
  donorName: string;
  donorFatherOrHusbandName?: string;
  donorRelation?: 'Father' | 'Husband' | string;
  donorAddress?: string;
  donorOfficeName?: string;
  donorCnic?: string;
  cashierName?: string;
  verificationCode?: string;
}

export interface ExpenseReceiptPayload {
  expenseId: number | string;
  name: string;
  amount: number;
  expenseTypeName: string;
  accountName?: string;
  userName?: string;
}

export interface IncomeReceiptPayload {
  incomeId: number | string;
  receiptId: string;
  verificationCode?: string;
  amount: number;
  departmentName: string;
  incomeTypeName: string;
  accountName?: string;
  operatorName?: string;
}

function getElectronAPI(): Window['electronAPI'] | undefined {
  return (window as Window & { electronAPI?: Window['electronAPI'] }).electronAPI;
}

function getPrintPrefs(): { language: 'en' | 'ur'; slipPrinter: string } {
  let language: 'en' | 'ur' = 'en';
  let slipPrinter = '';
  try {
    const prefs = JSON.parse(localStorage.getItem('hria-prefs') || '{}');
    if (prefs.reportLanguage === 'en' || prefs.reportLanguage === 'ur') language = prefs.reportLanguage;
    if (prefs.slipPrinter) slipPrinter = prefs.slipPrinter;
  } catch {
    // defaults
  }
  return { language, slipPrinter };
}

/** Build receipt payload from a persisted donation record (e.g. after edit). */
export function donationToReceiptPayload(donation: Donation): DonationReceiptPayload {
  return {
    receiptId: donation.receiptId,
    amount: donation.amount,
    ref: donation.ref,
    donationTypeName: donation.donationTypeName,
    donorName: donation.donorName,
    donorFatherOrHusbandName: donation.donorFatherOrHusbandName,
    donorRelation: donation.donorRelation,
    donorAddress: donation.donorAddress,
    donorOfficeName: donation.donorOfficeName,
    cashierName: donation.cashierName,
    verificationCode: donation.verificationCode,
  };
}

/**
 * Print donation receipt. When mandatory=true (new donation), throws on failure so
 * the caller can roll back the saved record. When mandatory=false (edit/other),
 * failures surface as warnings only.
 */
export async function printDonationReceipt(
  data: DonationReceiptPayload,
  options: { mandatory: boolean }
): Promise<{ success: boolean; printerName?: string }> {
  const api = getElectronAPI();
  if (!api?.printReceipt) {
    if (options.mandatory) {
      throw new Error('Printing not available');
    }
    return { success: false };
  }

  const { language, slipPrinter } = getPrintPrefs();
  const result = await api.printReceipt({ ...data, language, slipPrinter });

  if (result?.success) {
    if (!options.mandatory) {
      toast.success(`Receipt #${data.receiptId} printed.`);
    }
    return { success: true, printerName: result.printerName };
  }

  const err = result?.error || 'Print failed';
  if (options.mandatory) {
    throw new Error(err);
  }
  toast.warning(`Donation saved, but receipt did not print: ${err}`);
  return { success: false };
}

/** Best-effort expense slip after the expense is already persisted. */
export async function printExpenseReceipt(data: ExpenseReceiptPayload): Promise<void> {
  const api = getElectronAPI();
  if (!api?.printExpenseReceipt) {
    toast.success('Expense created successfully!');
    return;
  }

  const { language, slipPrinter } = getPrintPrefs();
  try {
    const result = await api.printExpenseReceipt({ ...data, language, slipPrinter });
    if (result?.success) {
      toast.success(`Expense #${data.expenseId} created — receipt printed.`);
    } else {
      toast.warning(`Expense #${data.expenseId} created, but receipt did not print: ${result?.error || 'unknown error'}`);
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'unknown error';
    toast.warning(`Expense #${data.expenseId} created, but printing failed: ${message}`);
  }
}

/**
 * Print income receipt.
 * When mandatory=true (new income), throws on failure so the caller can rollback.
 * When mandatory=false (reprint), failures surface as warnings only.
 */
export async function printIncomeReceipt(
  data: IncomeReceiptPayload,
  options: { mandatory: boolean }
): Promise<boolean> {
  const api = getElectronAPI();
  if (!api?.printIncomeReceipt) {
    if (options.mandatory) {
      throw new Error('Printing not available');
    }
    toast.warning('Income saved, but printing is not available in this environment.');
    return false;
  }

  const { language, slipPrinter } = getPrintPrefs();
  const result = await api.printIncomeReceipt({ ...data, language, slipPrinter });

  if (result?.success) {
    if (!options.mandatory) {
      toast.success(`Income receipt ${data.receiptId} reprinted.`);
    }
    return true;
  }

  const err = result?.error || 'Print failed';
  if (options.mandatory) {
    throw new Error(err);
  }
  toast.warning(`Income saved, but receipt did not print: ${err}`);
  return false;
}
